import React from 'react'
import styled from '../../utils/styled'

type TypographyProps = {
  variant: 'h1' | 'h2' | 'h3' | 'h4' | 'body1' | 'body2' | 'bold'
  margin?: string
  inline?: boolean
  textAlign?: 'left' | 'center' | 'right'
}

export const Typography: React.FC<TypographyProps> = ({ variant, margin, inline, textAlign, children }) => {
  switch (variant) {
    case 'h1':
      return (
        <StyledH1 margin={margin} inline={inline} textAlign={textAlign}>
          {children}
        </StyledH1>
      )
    case 'h2':
      return (
        <StyledH2 margin={margin} inline={inline} textAlign={textAlign}>
          {children}
        </StyledH2>
      )
    case 'h3':
      return (
        <StyledH3 margin={margin} inline={inline} textAlign={textAlign}>
          {children}
        </StyledH3>
      )
    case 'h4':
      return (
        <StyledH4 margin={margin} inline={inline} textAlign={textAlign}>
          {children}
        </StyledH4>
      )
    case 'body1':
      return (
        <StyledBody1 margin={margin} inline={inline} textAlign={textAlign}>
          {children}
        </StyledBody1>
      )
    case 'body2':
      return (
        <StyledBody2 margin={margin} inline={inline} textAlign={textAlign}>
          {children}
        </StyledBody2>
      )
    case 'bold':
      return (
        <StyledBold margin={margin} inline={inline} textAlign={textAlign}>
          {children}
        </StyledBold>
      )
    default:
      return (
        <StyledBody1 margin={margin} inline={inline} textAlign={textAlign}>
          {children}
        </StyledBody1>
      )
  }
}

const StyledH1 = styled('h1')<Omit<TypographyProps, 'variant'>>`
  color: ${props => props.theme.colors.headings};
  font-size: ${props => props.theme.fontSizes.h1};
  ${({ margin }) =>
    margin &&
    `
    margin-top: ${margin};
    margin-bottom: ${margin};
  `};
  ${({ inline }) => inline && 'display: inline'};
  ${({ textAlign }) => textAlign && `text-align: ${textAlign}`};
`

const StyledH2 = styled('h2')<Omit<TypographyProps, 'variant'>>`
  color: ${props => props.theme.colors.headings};
  font-size: ${props => props.theme.fontSizes.h2};
  ${({ margin }) =>
    margin &&
    `
    margin-top: ${margin};
    margin-bottom: ${margin};
  `};
  ${({ inline }) => inline && 'display: inline'};
  ${({ textAlign }) => textAlign && `text-align: ${textAlign}`};
`

const StyledH3 = styled('h3')<Omit<TypographyProps, 'variant'>>`
  color: ${props => props.theme.colors.headings};
  font-size: ${props => props.theme.fontSizes.h3};
  ${({ margin }) =>
    margin &&
    `
    margin-top: ${margin};
    margin-bottom: ${margin};
  `};
  ${({ inline }) => inline && 'display: inline'};
  ${({ textAlign }) => textAlign && `text-align: ${textAlign}`};
`

const StyledH4 = styled('h4')<Omit<TypographyProps, 'variant'>>`
  color: ${props => props.theme.colors.headings};
  font-size: ${props => props.theme.fontSizes.h4};
  ${({ margin }) =>
    margin &&
    `
    margin-top: ${margin};
    margin-bottom: ${margin};
  `};
  ${({ inline }) => inline && 'display: inline'};
  ${({ textAlign }) => textAlign && `text-align: ${textAlign}`};
`

const StyledBody1 = styled('p')<Omit<TypographyProps, 'variant'>>`
  color: ${props => props.theme.colors.body};
  font-size: ${props => props.theme.fontSizes.body1};
  ${({ margin }) =>
    margin &&
    `
    margin-top: ${margin};
    margin-bottom: ${margin};
  `};
  ${({ inline }) => inline && 'display: inline'};
  ${({ textAlign }) => textAlign && `text-align: ${textAlign}`};
`

const StyledBody2 = styled('p')<Omit<TypographyProps, 'variant'>>`
  color: ${props => props.theme.colors.body2};
  font-size: ${props => props.theme.fontSizes.body2};
  ${({ margin }) =>
    margin &&
    `
    margin-top: ${margin};
    margin-bottom: ${margin};
  `};
  ${({ inline }) => inline && 'display: inline'};
  ${({ textAlign }) => textAlign && `text-align: ${textAlign}`};
`

const StyledBold = styled('b')<Omit<TypographyProps, 'variant'>>`
  color: ${props => props.theme.colors.body};
  font-size: ${props => props.theme.fontSizes.body1};
  ${({ margin }) =>
    margin &&
    `
    margin-top: ${margin};
    margin-bottom: ${margin};
  `};
  ${({ inline }) => inline && 'display: inline'};
  ${({ textAlign }) => textAlign && `text-align: ${textAlign}`};
`
